import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

/**
 * BOM Drag-and-Drop Context
 * 
 * Shared drag infrastructure for:
 * - Part A: Search panel → BOM tree (add part at specific position)
 * - Part B: Tree reorder/reparent (move existing line)
 * 
 * Architecture:
 * - DragProvider wraps the BOM Editor
 * - useDragSource() hook for drag sources (search cards, tree rows)
 * - useDropTarget() hook for drop targets (tree rows, tree gaps)
 * - Ghost renderer positioned at cursor
 * - Drop zone calculator for between-rows vs onto-assembly
 * - Edge scroll for tree panel
 * - Validation engine for valid/invalid drops
 */

const DragContext = createContext(null);

// Drop position types
const DROP_POSITION = {
  BEFORE: 'before',   // Insert before this node (sibling)
  AFTER: 'after',     // Insert after this node (sibling)
  CHILD: 'child',     // Insert as last child of this node
  NONE: 'none',       // No valid drop
};

// Drag source types
const DRAG_SOURCE = {
  SEARCH_CARD: 'search_card',  // From search panel
  TREE_NODE: 'tree_node',      // From BOM tree
};

export function DragProvider({ children, lines, onAddPart, onReorderLine, isDraft }) {
  const [dragState, setDragState] = useState(null);
  const [ghostPos, setGhostPos] = useState({ x: 0, y: 0 });
  const [dropTarget, setDropTarget] = useState(null);
  const [dropPosition, setDropPosition] = useState(DROP_POSITION.NONE);
  const [dropValid, setDropValid] = useState(true);
  const [dropReason, setDropReason] = useState('');
  const scrollTimerRef = useRef(null);
  const treeContainerRef = useRef(null);
  const prefersReducedMotion = useRef(
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  );

  // Get all descendants of a node
  const getDescendants = useCallback((nodeId) => {
    const result = new Set();
    const stack = [nodeId];
    while (stack.length > 0) {
      const current = stack.pop();
      const children = lines.filter(l => l.parent_line_id === current);
      for (const child of children) {
        result.add(child.line_id);
        stack.push(child.line_id);
      }
    }
    return result;
  }, [lines]);

  // Validate a drop target
  const validateDrop = useCallback((source, targetLineId, position) => {
    if (!source || !isDraft) return { valid: false, reason: 'BOM is not in Draft status' };

    const targetLine = targetLineId ? lines.find(l => l.line_id === targetLineId) : null;

    // Dropping as child of a non-assembly, non-Make node
    if (position === DROP_POSITION.CHILD && targetLine) {
      if (targetLine.make_buy === 'Buy' && !targetLine.is_assembly) {
        return { valid: false, reason: 'Buy parts cannot have children' };
      }
      if (targetLine.make_buy === 'Consumable') {
        return { valid: false, reason: 'Consumable parts cannot have children' };
      }
    }

    // For tree reorder: check circular reference
    if (source.type === DRAG_SOURCE.TREE_NODE && targetLine) {
      const sourceId = source.data.line_id;
      // Can't drop on self
      if (targetLineId === sourceId) return { valid: false, reason: 'Cannot drop on self' };
      // Can't drop parent under its own descendant
      if (position === DROP_POSITION.CHILD) {
        const descendants = getDescendants(sourceId);
        if (descendants.has(targetLineId)) {
          return { valid: false, reason: 'Cannot move under own descendant (circular)' };
        }
      }
    }

    // Check max depth
    if (position === DROP_POSITION.CHILD && targetLine) {
      const targetDepth = targetLine.level_depth || 0;
      let sourceMaxDepth = 0;
      if (source.type === DRAG_SOURCE.TREE_NODE) {
        const descendants = getDescendants(source.data.line_id);
        for (const descId of descendants) {
          const desc = lines.find(l => l.line_id === descId);
          if (desc) {
            const relativeDepth = (desc.level_depth || 0) - (source.data.level_depth || 0);
            sourceMaxDepth = Math.max(sourceMaxDepth, relativeDepth);
          }
        }
      }
      if (targetDepth + 1 + sourceMaxDepth >= 10) {
        return { valid: false, reason: 'Maximum nesting depth (10) would be exceeded' };
      }
    }

    return { valid: true, reason: '' };
  }, [lines, isDraft, getDescendants]);

  // Start drag
  const startDrag = useCallback((source, e) => {
    if (!isDraft) return;
    setDragState(source);
    setGhostPos({ x: e.clientX, y: e.clientY });
    document.body.style.cursor = 'grabbing';
    document.body.style.userSelect = 'none';
  }, [isDraft]);

  // Update drag position (mousemove)
  const updateDrag = useCallback((e) => {
    if (!dragState) return;
    setGhostPos({ x: e.clientX, y: e.clientY });

    // Edge scroll
    if (treeContainerRef.current) {
      const rect = treeContainerRef.current.getBoundingClientRect();
      const edgeZone = 40;
      const scrollSpeed = 8;
      
      if (e.clientY < rect.top + edgeZone && e.clientY > rect.top) {
        treeContainerRef.current.scrollTop -= scrollSpeed;
      } else if (e.clientY > rect.bottom - edgeZone && e.clientY < rect.bottom) {
        treeContainerRef.current.scrollTop += scrollSpeed;
      }
    }
  }, [dragState]);

  // End drag
  const endDrag = useCallback(async () => {
    if (!dragState) return;

    if (dropTarget && dropValid) {
      const source = dragState;
      const targetLineId = dropTarget;
      const position = dropPosition;

      if (source.type === DRAG_SOURCE.SEARCH_CARD) {
        // Add part from search at specific position
        const targetLine = lines.find(l => l.line_id === targetLineId);
        let parentId = null;
        let sortOrder = 0;

        if (position === DROP_POSITION.CHILD) {
          parentId = targetLineId;
          const children = lines.filter(l => l.parent_line_id === targetLineId);
          sortOrder = children.length;
        } else if (position === DROP_POSITION.BEFORE || position === DROP_POSITION.AFTER) {
          parentId = targetLine?.parent_line_id || null;
          sortOrder = (targetLine?.sort_order || 0) + (position === DROP_POSITION.AFTER ? 1 : 0);
        }

        if (onAddPart) {
          await onAddPart(source.data, parentId, sortOrder);
        }
      } else if (source.type === DRAG_SOURCE.TREE_NODE) {
        // Reorder/reparent tree node
        const targetLine = lines.find(l => l.line_id === targetLineId);
        let newParentId = null;
        let newSortOrder = 0;

        if (position === DROP_POSITION.CHILD) {
          newParentId = targetLineId;
          const children = lines.filter(l => l.parent_line_id === targetLineId);
          newSortOrder = children.length;
        } else if (position === DROP_POSITION.BEFORE || position === DROP_POSITION.AFTER) {
          newParentId = targetLine?.parent_line_id || null;
          newSortOrder = (targetLine?.sort_order || 0) + (position === DROP_POSITION.AFTER ? 1 : 0);
        }

        if (onReorderLine) {
          await onReorderLine(source.data.line_id, newParentId, newSortOrder);
        }
      }
    }

    // Reset state
    setDragState(null);
    setDropTarget(null);
    setDropPosition(DROP_POSITION.NONE);
    setDropValid(true);
    setDropReason('');
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, [dragState, dropTarget, dropValid, dropPosition, lines, onAddPart, onReorderLine]);

  // Cancel drag
  const cancelDrag = useCallback(() => {
    setDragState(null);
    setDropTarget(null);
    setDropPosition(DROP_POSITION.NONE);
    setDropValid(true);
    setDropReason('');
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  // Set drop target with validation
  const setDropInfo = useCallback((targetLineId, position) => {
    if (!dragState) return;
    setDropTarget(targetLineId);
    setDropPosition(position);
    const validation = validateDrop(dragState, targetLineId, position);
    setDropValid(validation.valid);
    setDropReason(validation.reason);
  }, [dragState, validateDrop]);

  // Global event listeners
  useEffect(() => {
    if (!dragState) return;

    const handleMouseMove = (e) => updateDrag(e);
    const handleMouseUp = () => endDrag();
    const handleKeyDown = (e) => { if (e.key === 'Escape') cancelDrag(); };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [dragState, updateDrag, endDrag, cancelDrag]);

  const value = {
    dragState,
    ghostPos,
    dropTarget,
    dropPosition,
    dropValid,
    dropReason,
    startDrag,
    setDropInfo,
    cancelDrag,
    treeContainerRef,
    isDragging: !!dragState,
    prefersReducedMotion: prefersReducedMotion.current,
    DRAG_SOURCE,
    DROP_POSITION,
  };

  return (
    <DragContext.Provider value={value}>
      {children}
      {/* Ghost Renderer */}
      {dragState && (
        <div
          className="fixed z-[9999] pointer-events-none"
          style={{
            left: ghostPos.x + 12,
            top: ghostPos.y - 16,
            opacity: 0.9,
            transform: 'scale(0.95)',
          }}
        >
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 border border-blue-500/50 rounded-lg shadow-xl text-xs max-w-[280px]">
            {dragState.type === DRAG_SOURCE.SEARCH_CARD ? (
              <>
                <span className="text-emerald-400 font-bold">+</span>
                <span className="font-mono text-cyan-300 text-[11px]">{dragState.data.hpn_display || dragState.data.hpn_compact}</span>
                <span className="text-slate-400 truncate">{dragState.data.description}</span>
              </>
            ) : (
              <>
                <span className="text-blue-400 font-bold">{dragState.data.is_assembly ? '◆' : '■'}</span>
                <span className="font-mono text-cyan-300 text-[11px]">{dragState.data.assembly_number || dragState.data.part_number || dragState.data.part_id}</span>
                <span className="text-slate-400 truncate">{dragState.data.is_assembly ? dragState.data.assembly_name : dragState.data.part_description}</span>
              </>
            )}
          </div>
          {!dropValid && dropReason && (
            <div className="mt-1 px-2 py-1 bg-red-900/90 border border-red-500/50 rounded text-[10px] text-red-300 max-w-[240px]">
              {dropReason}
            </div>
          )}
        </div>
      )}
    </DragContext.Provider>
  );
}

export function useDragContext() {
  return useContext(DragContext);
}

export { DROP_POSITION, DRAG_SOURCE };
